import type { ThemeOptions, PaletteType, Theme as MuiTheme } from "@material-ui/core";
import { createTheme, MuiThemeProvider } from "@material-ui/core";
import type { PaletteOptions as MuiPaletteOptions, Palette as MuiPalette } from "@material-ui/core/styles/createPalette";
import { colorScales, fontFamily, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ReactNode } from "react";
import React from "react";
interface ExtraPaletteOptions {
    status: StatusColors;
}
type OctopusPaletteOptions = ExtraPaletteOptions & MuiPaletteOptions;
interface StatusColors {
    danger: string;
    info: string;
}
interface OctopusPalette extends MuiPalette {
    status: StatusColors;
}
interface Theme extends MuiTheme {
    palette: OctopusPalette;
}
export function MuiThemeApplier({ themeName, children }: {
    themeName: "light" | "dark";
    children: ReactNode;
}) {
    const muiTheme = React.useMemo(() => createMuiTheme(themeName), [themeName]);
    return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
}
function createLightPalette(): OctopusPaletteOptions {
    return {
        type: "light",
        primary: { main: colorScales.blue["500"] },
        secondary: {
            main: colorScales.blue["500"],
            contrastText: colorScales.white,
        },
        text: {
            // The most important text.
            primary: colorScales.grey["800"],
            // Secondary text.
            secondary: colorScales.grey["500"],
            // Disabled text have even lower visual prominence.
            disabled: colorScales.grey["500"],
            // Text hints.
            hint: colorScales.grey["600"],
        },
        background: {
            paper: colorScales.white,
            default: colorScales.white,
        },
        divider: colorScales.grey["200"],
        // The colors used to style the action elements.
        action: {
            // The color of an active action like an icon button.
            active: colorScales.grey["800"],
            // The color of an hovered action.
            hover: colorScales.grey["200"],
            hoverOpacity: 0.08,
            // The color of a selected action.
            selected: colorScales.grey["200"],
            // The color of a disabled action.
            disabled: colorScales.grey["500"],
            // The background color of a disabled action.
            disabledBackground: colorScales.grey["200"],
        },
        error: {
            main: colorScales.red["500"],
        },
        status: {
            danger: colorScales.orange["500"],
            info: colorScales.blue["500"],
        },
    };
}
function createDarkPalette(): OctopusPaletteOptions {
    return {
        type: "dark",
        primary: { main: colorScales.blue["400"] },
        secondary: {
            main: colorScales.blue["400"],
        },
        background: {
            paper: colorScales.navy["800"],
            default: colorScales.navy["800"],
        },
        text: {
            // The most important text.
            primary: colorScales.blue["100"],
            // Secondary text.
            secondary: colorScales.navy["300"],
            // Disabled text have even lower visual prominence.
            disabled: colorScales.navy["300"],
            // Text hints.
            hint: colorScales.navy["200"],
        },
        divider: colorScales.navy["700"],
        error: {
            main: colorScales.red["500"],
        },
        status: {
            danger: colorScales.orange["500"],
            info: colorScales.blue["500"],
        },
    };
}
function createMuiTheme(type: PaletteType): MuiTheme {
    const palette = type === "dark" ? createDarkPalette() : createLightPalette();
    const options: ThemeOptions = {
        palette,
        typography: {
            fontFamily: fontFamily.default,
        },
        overrides: {
            MuiMenu: {
                paper: {
                    backgroundColor: themeTokens.color.background.secondary.hovered,
                },
            },
            MuiDivider: {
                root: {
                    backgroundColor: themeTokens.color.border.primary,
                },
            },
            MuiPopover: {
                paper: {
                    boxShadow: themeTokens.shadow.medium,
                },
            },
            MuiDialog: {
                paper: {
                    boxShadow: themeTokens.shadow.large,
                },
            },
            MuiInputBase: {
                root: {
                    color: themeTokens.color.textField.text.primary,
                },
                input: {
                    "&::placeholder": {
                        color: themeTokens.color.textField.text.secondary,
                    },
                },
            },
            MuiFormLabel: {
                root: {
                    color: themeTokens.color.textField.text.secondary,
                    font: text.body.regular.large,
                    "&$focused": {
                        color: themeTokens.color.textField.text.selected,
                    },
                    "&$error": {
                        color: themeTokens.color.textField.text.danger,
                    },
                },
            },
            MuiInputLabel: {
                root: {
                    color: themeTokens.color.textField.text.secondary,
                },
                shrink: {
                    color: themeTokens.color.textField.text.secondary,
                },
            },
            MuiInput: {
                underline: {
                    "&:before": {
                        borderBottomColor: themeTokens.color.textField.border.default,
                    },
                    "&:hover:not($disabled):before": {
                        borderBottomColor: themeTokens.color.textField.border.hover,
                        borderBottomWidth: "2px",
                    },
                    "&:after": {
                        borderBottomColor: themeTokens.color.textField.border.active,
                    },
                    "&$error": {
                        "&:after": {
                            borderBottomColor: themeTokens.color.textField.text.danger,
                        },
                    },
                },
                input: {
                    "&::placeholder": {
                        opacity: 1,
                    },
                },
            },
        },
    };
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return createTheme(options) as Theme;
}
